import React, { useEffect, useState, useCallback } from "react";
import { collection, getDocs, getDoc, doc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FaWhatsapp, FaCheck, FaClock, FaExclamationTriangle, FaPause, FaPlay, FaSync, FaEye, FaPenSquare, FaPaperPlane, FaSeedling } from "react-icons/fa";
import { db } from "../../firebase";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import AdminModal from "../../components/AdminModal";
import { useToast } from "../../components/Toast";
import { colors, spacing, typography, radius, shadows, pageContainer, pageTitle, pageSubtitle } from "../../utils/designTokens";
import { COMMUNICATION_TEMPLATES } from "../../utils/communicationTemplates";
import { sembrarPlantillas } from "../../utils/plantillasSiembra";

/**
 * Pantalla SuperAdmin: vista read-only de los templates HSM de WhatsApp.
 *
 * MVP:
 *   - Lista los templates desde notificationTemplates collection.
 *   - Muestra metaStatus (APPROVED/PENDING/REJECTED), categoria, idioma,
 *     ultima sincronizacion con Meta.
 *   - Activar/pausar (afecta a TODOS los edificios; granularidad por edificio
 *     viene en sprint 3).
 *   - Preview del body con variables.
 *
 * NO incluido (sprint 3):
 *   - Edicion del body (requiere re-aprobacion de Meta).
 *   - Configuracion por edificio (buildingTemplateConfig).
 *   - Estadisticas de envio (entregados/leidos/fallidos por template).
 */

const STATUS_BADGES = {
  APPROVED: { label: "Aprobado", icon: FaCheck, color: colors.success, bg: colors.successLight },
  PENDING: { label: "En revisión Meta", icon: FaClock, color: colors.warningDark, bg: colors.warningLight },
  PENDING_REVIEW: { label: "En revisión Meta", icon: FaClock, color: colors.warningDark, bg: colors.warningLight },
  PENDING_SUBMIT: { label: "Por enviar a Meta", icon: FaPaperPlane, color: colors.infoDark, bg: colors.infoLight },
  REJECTED: { label: "Rechazado", icon: FaExclamationTriangle, color: colors.error, bg: colors.errorLight },
  PAUSED: { label: "Pausado", icon: FaPause, color: colors.gray700, bg: colors.gray100 },
};

const PlantillasWhatsApp = () => {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editExamples, setEditExamples] = useState([]);
  const [submittingRev, setSubmittingRev] = useState(false);
  const [seeding, setSeeding] = useState(false);
  // Vista: "managed" = catálogo Firestore (gestión); "meta" = TODAS las
  // plantillas que existen en la WABA de Meta (fuente de verdad), incluidas
  // las de activación (nombre distinto), gasto_vecino, cobranza y comerciales.
  const [view, setView] = useState("managed");
  const [metaTemplates, setMetaTemplates] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [onlyActiveApproved, setOnlyActiveApproved] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "notificationTemplates"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Ordenar por key (alfabetico) para que aparezca consistente
      data.sort((a, b) => (a.key || a.id).localeCompare(b.key || b.id));
      setTemplates(data);
    } catch (e) {
      console.error("PlantillasWhatsApp fetch error:", e);
      showToast("Error cargando plantillas", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Listado completo desde Meta (callable whatsappListTemplates, SuperAdmin).
  const fetchMetaTemplates = useCallback(async () => {
    setMetaLoading(true);
    try {
      const fns = getFunctions();
      const call = httpsCallable(fns, "whatsappListTemplates");
      const res = await call({});
      setMetaTemplates(res.data?.templates || []);
    } catch (e) {
      console.error("whatsappListTemplates error:", e);
      showToast(e.message || "Error consultando plantillas en Meta", "error");
      setMetaTemplates([]);
    } finally {
      setMetaLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (view === "meta" && metaTemplates === null) fetchMetaTemplates();
  }, [view, metaTemplates, fetchMetaTemplates]);

  // Sembrar los templates definidos en COMMUNICATION_TEMPLATES (catálogo
  // estático del código) hacia Firestore. Solo crea (utils/plantillasSiembra):
  // id == key, active false y source 'catalog_seed', lo que piden las reglas
  // 0D; una plantilla que ya existe no se toca (no pisa cambios del SuperAdmin).
  const seedFromCatalog = async () => {
    if (seeding) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Esto va a crear los ${COMMUNICATION_TEMPLATES.length} templates del catálogo en Firestore (los existentes no se modifican). ¿Continuar?`)) {
      return;
    }
    setSeeding(true);
    try {
      const r = await sembrarPlantillas(COMMUNICATION_TEMPLATES, {
        existentes: new Set(templates.map((t) => t.key || t.id)),
        existe: async (key) => (await getDoc(doc(db, "notificationTemplates", key))).exists(),
        crear: (key, datos) => setDoc(doc(db, "notificationTemplates", key), datos),
        ts: serverTimestamp(),
      });
      const resumen = `Templates sembrados: ${r.creadas.length} nuevos, ${r.salteadas.length} ya existían.`;
      if (r.fallidas.length) {
        showToast(`${resumen} No se pudieron crear: ${r.fallidas.join(", ")}.`, "warning");
      } else {
        showToast(resumen, "success");
      }
      await fetchTemplates();
    } catch (e) {
      console.error("seedFromCatalog error:", e);
      showToast(`Error al sembrar: ${e.message}`, "error");
    } finally {
      setSeeding(false);
    }
  };

  const toggleActive = async (template) => {
    const newActive = !template.active;
    try {
      await updateDoc(doc(db, "notificationTemplates", template.id), {
        active: newActive,
        updatedAt: serverTimestamp(),
      });
      showToast(
        newActive ? `Plantilla "${template.key}" activada` : `Plantilla "${template.key}" pausada`,
        "success"
      );
      await fetchTemplates();
    } catch (e) {
      console.error("toggleActive error:", e);
      showToast(`Error: ${e.message}`, "error");
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderBodyPreview = (body, exampleValues) => {
    if (!body) return "—";
    let preview = body;
    if (exampleValues && exampleValues.length > 0) {
      exampleValues.forEach((val, i) => {
        preview = preview.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, "g"), val);
      });
    }
    return preview;
  };

  const exampleValuesByKey = {
    nueva_expensa: ["Juan", "Octubre 2026", "Edificio Torre Sur", "4.250", "15/11/2026"],
    recordatorio_pago: ["Juan", "4.250", "Edificio Torre Sur", "15/11/2026", "en 3 días"],
    respuesta_incidencia: ["Juan", "Edificio Torre Sur", "Pérdida de agua", "Coordinamos plomero para mañana"],
    nueva_novedad: ["Juan", "Edificio Torre Sur", "Corte de agua programado mañana 9-12hs"],
    pago_aprobado: ["Juan", "Edificio Torre Sur", "Gasto Común octubre", "4.250"],
    pago_rechazado: ["Juan", "Edificio Torre Sur", "4.250", "Comprobante ilegible"],
    multa_aplicada: ["Juan", "Edificio Torre Sur", "1.500", "Ruidos molestos después de las 22hs"],
    reserva_amenity_confirmada: ["Juan", "SUM", "Edificio Torre Sur", "15/11/2026", "20:00"],
    reserva_amenity_recordatorio: ["Juan", "SUM", "15/11/2026", "20:00", "Edificio Torre Sur"],
    incidencia_creada: ["Juan", "Edificio Torre Sur", "Pérdida de agua en cocina"],
    asamblea_convocatoria: ["Juan", "Edificio Torre Sur", "15/11/2026", "19:00", "modalidad presencial"],
  };

  const openEdit = (template) => {
    setPreviewing(null);
    setEditing(template);
    setEditBody(template.body || "");
    const examples = exampleValuesByKey[template.key] || (template.variableSchema || []).map(() => "");
    setEditExamples([...examples]);
  };

  const closeEdit = () => {
    if (submittingRev) return;
    setEditing(null);
    setEditBody("");
    setEditExamples([]);
  };

  const submitRevision = async () => {
    if (!editing) return;
    const expectedCount = (editing.variableSchema || []).length;
    const placeholders = editBody.match(/\{\{(\d+)\}\}/g) || [];
    const uniquePositions = new Set(placeholders.map((p) => p.replace(/[{}]/g, "")));
    if (uniquePositions.size !== expectedCount) {
      showToast(
        `Necesitas ${expectedCount} variables ({{1}}..{{${expectedCount}}}). Encontre ${uniquePositions.size}.`,
        "error"
      );
      return;
    }
    if (editExamples.some((e) => !String(e || "").trim())) {
      showToast("Completa todos los ejemplos (uno por variable)", "error");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm("Esto envía una versión nueva a Meta. Mientras Meta aprueba (24-48h) se sigue usando la actual. ¿Continuar?")) {
      return;
    }
    setSubmittingRev(true);
    try {
      const fns = getFunctions();
      const callable = httpsCallable(fns, "submitTemplateRevision");
      const result = await callable({
        templateKey: editing.key,
        newBody: editBody,
        newExamples: editExamples,
      });
      showToast(
        result.data?.message || "Revisión enviada a Meta",
        "success"
      );
      closeEdit();
      await fetchTemplates();
    } catch (e) {
      console.error("submitTemplateRevision error:", e);
      showToast(`Error: ${e.message}`, "error");
    } finally {
      setSubmittingRev(false);
    }
  };

  const columns = [
    {
      key: "key",
      label: "Plantilla",
      render: (val, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 400, color: colors.gray900 }}>{val}</span>
          {row.description && (
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>
              {row.description}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "metaStatus",
      label: "Estado Meta",
      align: "center",
      render: (val) => {
        const badge = STATUS_BADGES[val] || { label: val || "—", icon: FaClock, color: colors.gray600, bg: colors.gray100 };
        const Icon = badge.icon;
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "3px 10px",
            background: badge.bg,
            color: badge.color,
            borderRadius: radius.full,
            fontSize: typography.sizes.sm,
          }}>
            <Icon size={10} />
            {badge.label}
          </span>
        );
      },
    },
    {
      key: "metaCategory",
      label: "Categoria",
      align: "center",
      render: (val, row) => val || row.category || "UTILITY",
    },
    {
      key: "language",
      label: "Idioma",
      align: "center",
      render: (val) => (val || "es").toUpperCase(),
    },
    {
      key: "metaSyncedAt",
      label: "Ultima sync",
      align: "center",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
          {formatTimestamp(val)}
        </span>
      ),
    },
    {
      key: "active",
      label: "Activa",
      align: "center",
      render: (v, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggleActive(row); }}
          style={{
            ...iconBtnStyle,
            background: v !== false ? colors.successLight : colors.gray100,
            color: v !== false ? colors.successDark : colors.gray600,
          }}
          title={v !== false ? "Pausar plantilla" : "Activar plantilla"}
        >
          {v !== false ? <FaPause size={11} /> : <FaPlay size={11} />}
        </button>
      ),
    },
    {
      key: "_actions",
      label: "",
      align: "right",
      render: (_, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setPreviewing(row); }}
          style={iconBtnStyle}
          title="Ver preview"
        >
          <FaEye size={12} />
        </button>
      ),
    },
  ];

  const summaryByStatus = templates.reduce((acc, t) => {
    const k = t.metaStatus || "UNKNOWN";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  // Columnas de la vista "Todas (Meta)": lo que existe en la WABA, con o sin
  // gestión en Firestore.
  const metaColumns = [
    {
      key: "name",
      label: "Nombre en Meta",
      render: (val, row) => (
        <div>
          <div style={{ color: colors.gray900 }}>{val}</div>
          {row.managedLabel && (
            <div style={{ fontSize: typography.sizes.xs, color: colors.gray500 }}>
              {row.managedLabel}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "language",
      label: "Idioma",
      align: "center",
      render: (val) => (val || "es").toUpperCase(),
    },
    {
      key: "status",
      label: "Estado Meta",
      align: "center",
      render: (val) => {
        const badge = STATUS_BADGES[val] || { label: val || "—", icon: FaClock, color: colors.gray600, bg: colors.gray100 };
        const Icon = badge.icon;
        return (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 10px", background: badge.bg, color: badge.color,
            borderRadius: radius.full, fontSize: typography.sizes.sm,
          }}>
            <Icon size={10} />
            {badge.label}
          </span>
        );
      },
    },
    { key: "category", label: "Categoria", align: "center", render: (v) => v || "—" },
    {
      key: "trigger",
      label: "La envía",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
          {val || "—"}
        </span>
      ),
    },
    {
      key: "managedKey",
      label: "Gestión",
      align: "center",
      render: (val, row) =>
        val ? (
          <span style={{ fontSize: typography.sizes.sm, color: row.active === false ? colors.gray500 : colors.successDark }}>
            {row.active === false ? "Pausada" : "Activa"}
          </span>
        ) : (
          <span style={{ fontSize: typography.sizes.sm, color: colors.warningDark }}>
            Sin doc Firestore
          </span>
        ),
    },
  ];

  const metaFiltered = (metaTemplates || []).filter((t) =>
    onlyActiveApproved ? t.status === "APPROVED" && t.active !== false : true
  );

  if (loading) return <Loader />;

  return (
    <div style={pageContainer}>
      <div style={headerStyle}>
        <div>
          <h1 style={pageTitle}>Plantillas de WhatsApp</h1>
          <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
            Plantillas HSM aprobadas por Meta que se usan para los avisos
            transaccionales (gastos comunes, multas, incidencias, etc.). El estado se
            sincroniza con Meta cada 30 minutos automáticamente.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={seedFromCatalog}
            disabled={seeding}
            style={{ ...ghostBtnStyle, opacity: seeding ? 0.6 : 1, cursor: seeding ? "wait" : "pointer" }}
            title="Crea los templates del catálogo (cobranza, recordatorios, etc.) en Firestore si todavía no existen."
          >
            <FaSeedling size={11} />
            {seeding ? "Sembrando…" : "Sembrar del catálogo"}
          </button>
          <button onClick={fetchTemplates} style={ghostBtnStyle}>
            <FaSync size={11} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Tabs: catálogo gestionado vs listado completo en Meta */}
      <div style={{ display: "flex", gap: 8, marginBottom: spacing[5] }}>
        {[
          { id: "managed", label: "Gestionadas" },
          { id: "meta", label: "Todas (Meta)" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            style={{
              ...ghostBtnStyle,
              background: view === t.id ? colors.primary : colors.white,
              color: view === t.id ? colors.white : colors.gray700,
              borderColor: view === t.id ? colors.primary : colors.gray200,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === "meta" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing[4], flexWrap: "wrap", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: typography.sizes.sm, color: colors.gray700, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={onlyActiveApproved}
                onChange={(e) => setOnlyActiveApproved(e.target.checked)}
                aria-label="Mostrar solo plantillas activas y aprobadas"
              />
              Solo activas y aprobadas
            </label>
            <button onClick={fetchMetaTemplates} style={ghostBtnStyle} disabled={metaLoading}>
              <FaSync size={11} />
              {metaLoading ? "Consultando Meta…" : "Actualizar desde Meta"}
            </button>
          </div>
          {metaLoading && metaTemplates === null ? (
            <Loader />
          ) : metaFiltered.length === 0 ? (
            <EmptyState
              icon={FaWhatsapp}
              title="Sin plantillas para mostrar"
              description={onlyActiveApproved
                ? "Ninguna plantilla está aprobada y activa con el filtro puesto."
                : "Meta no devolvió plantillas para la WABA configurada."}
            />
          ) : (
            <DataTable columns={metaColumns} data={metaFiltered} />
          )}
        </div>
      )}

      {view === "managed" && (<>
      {/* Resumen por status */}
      <div style={{ display: "flex", gap: spacing[3], marginBottom: spacing[5], flexWrap: "wrap" }}>
        {Object.entries(STATUS_BADGES).map(([status, cfg]) => {
          const count = summaryByStatus[status] || 0;
          const Icon = cfg.icon;
          return (
            <div key={status} style={{
              padding: `${spacing[3]}px ${spacing[4]}px`,
              background: colors.white,
              borderRadius: radius.md,
              boxShadow: shadows.xs,
              minWidth: 160,
              display: "flex",
              alignItems: "center",
              gap: spacing[3],
            }}>
              <div style={{
                width: 36, height: 36,
                borderRadius: radius.sm,
                background: cfg.bg,
                color: cfg.color,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon size={14} />
              </div>
              <div>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
                  {cfg.label}
                </div>
                <div style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.gray900 }}>
                  {count}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {templates.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <EmptyState
            icon={FaWhatsapp}
            title="No hay plantillas cargadas"
            description="Sembrá las plantillas del catálogo de Habitia o ejecutá seed-notification-templates.js desde functions/."
          />
          <button
            onClick={seedFromCatalog}
            disabled={seeding}
            style={{ ...primaryBtnStyle, opacity: seeding ? 0.6 : 1 }}
          >
            <FaSeedling size={12} />
            {seeding ? "Sembrando…" : `Sembrar ${COMMUNICATION_TEMPLATES.length} plantillas del catálogo`}
          </button>
        </div>
      ) : (
        <DataTable columns={columns} data={templates} onRowClick={setPreviewing} />
      )}
      </>)}

      <AdminModal
        open={!!previewing}
        onClose={() => setPreviewing(null)}
        title={previewing ? `Preview: ${previewing.key}` : ""}
        size="medium"
        icon={<FaWhatsapp size={20} color="#25D366" />}
        footer={previewing && (
          <div style={{ display: "flex", gap: 8, justifyContent: "space-between", width: "100%", flexWrap: "wrap" }}>
            <button onClick={() => openEdit(previewing)} style={ghostBtnStyle}>
              <FaPenSquare size={11} />
              Editar copy
            </button>
            <button onClick={() => setPreviewing(null)} style={primaryBtnStyle}>
              Cerrar
            </button>
          </div>
        )}
      >
        {previewing && (
          <>
            <div style={{ marginBottom: spacing[4] }}>
              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 4 }}>
                Mensaje como lo veria el residente:
              </div>
              <div style={{
                padding: spacing[4],
                background: "#DCF8C6",
                borderRadius: radius.md,
                fontSize: typography.sizes.base,
                lineHeight: 1.5,
                color: colors.gray900,
                whiteSpace: "pre-wrap",
                fontFamily: "'FT Habit', sans-serif",
              }}>
                {renderBodyPreview(previewing.body, exampleValuesByKey[previewing.key])}
              </div>
              <div style={{ fontSize: 11, color: colors.gray400, marginTop: 6 }}>
                Datos de ejemplo. Las variables reales se inyectan al enviar.
              </div>
            </div>

            <div style={{ marginBottom: spacing[3] }}>
              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 4 }}>
                Variables ({(previewing.variableSchema || []).length}):
              </div>
              <table style={{ width: "100%", fontSize: typography.sizes.sm, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.gray200}`, color: colors.gray600 }}>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Pos</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Nombre</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Descripcion</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewing.variableSchema || []).map((v, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.gray100}` }}>
                      <td style={{ padding: "6px 8px", color: colors.primary }}>{`{{${v.position}}}`}</td>
                      <td style={{ padding: "6px 8px", color: colors.gray900 }}>{v.name}</td>
                      <td style={{ padding: "6px 8px", color: colors.gray600 }}>{v.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ paddingTop: spacing[3], borderTop: `1px solid ${colors.gray200}`, fontSize: typography.sizes.sm, color: colors.gray600 }}>
              <div>Nombre Meta: <code>{previewing.metaTemplateName}</code></div>
              <div>Categoría: {previewing.metaCategory || previewing.category}</div>
              <div>Idioma: {(previewing.language || "es").toUpperCase()}</div>
              {previewing.metaTemplateId && (
                <div>ID Meta: <code>{previewing.metaTemplateId}</code></div>
              )}
              {previewing.version && (
                <div>Versión actual: v{previewing.version}</div>
              )}
              {previewing.pendingRevision && (
                <div style={{
                  marginTop: 6,
                  padding: 8,
                  background: colors.warningLight,
                  color: colors.warningDark,
                  borderRadius: radius.sm,
                  fontSize: typography.sizes.sm,
                }}>
                  Hay una revisión pendiente de Meta:
                  <strong> {previewing.pendingRevision.newMetaName}</strong>
                  {" "}({previewing.pendingRevision.metaStatus || "PENDING"}).
                  Cuando se apruebe, reemplaza la actual automáticamente.
                </div>
              )}
            </div>
          </>
        )}
      </AdminModal>

      {/* Modal de edición del copy con re-aprobación de Meta */}
      <AdminModal
        open={!!editing}
        onClose={closeEdit}
        title={editing ? `Editar copy: ${editing.key}` : ""}
        size="wide"
        icon={<FaPenSquare size={18} color={colors.primary} />}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={closeEdit} disabled={submittingRev} style={ghostBtnStyle}>
              Cancelar
            </button>
            <button onClick={submitRevision} disabled={submittingRev} style={primaryBtnStyle}>
              {submittingRev ? "Enviando a Meta..." : "Solicitar aprobación"}
            </button>
          </div>
        }
      >
        {editing && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            <div style={{
              padding: spacing[3],
              background: colors.warningLight,
              color: colors.warningDark,
              borderRadius: radius.sm,
              fontSize: typography.sizes.sm,
              lineHeight: 1.5,
            }}>
              ⚠️ Cambiar el copy crea una versión nueva del template en Meta.
              Tarda 24-48h en aprobar (a veces minutos). Mientras tanto se sigue
              usando la versión actual <code>{editing.metaTemplateName}</code>.
              <br />
              <strong>Importante:</strong> mantené la cantidad y orden de las
              variables {`{{1}}, {{2}}, ...`} ({(editing.variableSchema || []).length} en total).
              Si cambiás la cantidad u orden, los triggers van a romperse al inyectar datos.
            </div>

            <div>
              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 4 }}>
                Body del template:
              </div>
              <textarea
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={6}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: typography.sizes.base,
                  fontWeight: 400,
                  color: colors.gray900,
                  background: colors.white,
                  border: `1px solid ${colors.gray300}`,
                  borderRadius: radius.sm,
                  outline: "none",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ fontSize: 11, color: colors.gray400, marginTop: 4 }}>
                {editBody.length} / 1024 caracteres
              </div>
            </div>

            <div>
              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[2] }}>
                Datos de ejemplo (uno por variable, requeridos por Meta para evaluar):
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[2] }}>
                {(editing.variableSchema || []).map((v, i) => (
                  <div key={i}>
                    <label style={{
                      fontSize: 11,
                      color: colors.gray600,
                      display: "block",
                      marginBottom: 2,
                    }}>
                      <code style={{ color: colors.primary }}>{`{{${v.position}}}`}</code>{" "}
                      {v.name} — {v.description}
                    </label>
                    <input
                      type="text"
                      value={editExamples[i] || ""}
                      onChange={(e) => {
                        const next = [...editExamples];
                        next[i] = e.target.value;
                        setEditExamples(next);
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontSize: typography.sizes.base,
                        color: colors.gray900,
                        background: colors.white,
                        border: `1px solid ${colors.gray300}`,
                        borderRadius: radius.sm,
                        outline: "none",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 4 }}>
                Preview con tus ejemplos:
              </div>
              <div style={{
                padding: spacing[3],
                background: "#DCF8C6",
                borderRadius: radius.md,
                fontSize: typography.sizes.base,
                lineHeight: 1.5,
                color: colors.gray900,
                whiteSpace: "pre-wrap",
              }}>
                {renderBodyPreview(editBody, editExamples)}
              </div>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.white,
  background: colors.primary,
  border: "none",
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: spacing[4],
  gap: spacing[4],
};

const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray700,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const iconBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  background: colors.gray100,
  border: "none",
  borderRadius: radius.sm,
  cursor: "pointer",
  color: colors.gray700,
};

export default PlantillasWhatsApp;
